import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_BILDSCHIRMPRAESENTATION, MAKRO_UEBERGAENGE } from "../makros";

export const lektionAnzeigedauern: Lesson = {
  id: "anzeigedauern",
  title: "Anzeigedauern und Aufzeichnen",
  summary: "Eine Präsentation, die von selbst läuft – im richtigen Tempo.",
  durationMinutes: 9,
  learningGoals: [
    "Anzeigedauern mit „Neue Anzeigedauern testen“ einüben",
    "Eine Präsentation mit Sprechertext aufzeichnen",
  ],
  steps: [
    {
      id: "probelauf",
      title: "Neue Anzeigedauern testen",
      explanation: [
        "„Bildschirmpräsentation“ → „Neue Anzeigedauern testen“ startet die Präsentation mit " +
          "einer Stoppuhr. Du klickst weiter, wenn eine Folie lange genug zu sehen war; am " +
          "Ende fragt PowerPoint, ob die Zeiten übernommen werden sollen. Jede Folie bekommt " +
          "dann unter „Übergänge“ → „Nach“ ihre Dauer.",
        "Das ist der sicherste Weg zu passenden Zeiten: Wer beim Probelauf den Text auf der " +
          "Folie einmal selbst liest, gibt dem Publikum genau so viel Zeit, wie es braucht.",
        "Für einen Vortrag mit Redner sind Anzeigedauern meist unerwünscht. In " +
          "„Bildschirmpräsentation einrichten“ legt „Manuell“ oder „Anzeigedauern verwenden“ " +
          "fest, ob sie beachtet werden.",
      ],
      keyPoints: [
        "Bildschirmpräsentation → Neue Anzeigedauern testen.",
        "Die Zeiten landen unter Übergänge → Nach.",
        "„Anzeigedauern verwenden“ in der Einrichtung schaltet sie wirksam.",
      ],
      task: {
        instruction:
          "Übe in deiner Kiosk-Kopie mit „Neue Anzeigedauern testen“ für alle Folien eine Anzeigedauer ein und übernimm sie.",
        hint: "Bildschirmpräsentation → Neue Anzeigedauern testen, durchklicken, am Ende „Ja“.",
        macro: MAKRO_UEBERGAENGE,
        check: () =>
          pruefeMitMakro(MAKRO_UEBERGAENGE, (bericht) => {
            const folien = [...bericht.gruppe("folie").values()];
            const sichtbar = folien.filter((folie) => folie.get("ausgeblendet") !== "1");
            const mitZeit = sichtbar.filter((folie) => folie.get("zeit") === "1" && Number(folie.get("sekunden") ?? 0) > 0);
            return fromCriteria(
              [
                criterion(
                  `Alle sichtbaren Folien haben eine Anzeigedauer (${mitZeit.length} von ${sichtbar.length})`,
                  sichtbar.length > 0 && mitZeit.length === sichtbar.length,
                ),
              ],
              {
                success: "Jede Folie weiß jetzt, wie lange sie stehen bleibt.",
                failure: "Noch nicht alle Folien haben eine Anzeigedauer.",
                hint: "Beim Probelauf am Ende die Frage nach dem Speichern der Anzeigedauern mit „Ja“ beantworten.",
              },
            );
          }),
      },
    },
    {
      id: "anzeigedauern-verwenden",
      title: "Anzeigedauern verwenden",
      explanation: [
        "Die Zeiten allein genügen nicht: Erst „Bildschirmpräsentation einrichten“ → " +
          "„Anzeigedauern verwenden, wenn vorhanden“ lässt die Präsentation danach laufen. " +
          "Bei der Kiosk-Art ist das Pflicht, sonst bleibt die erste Folie für immer stehen.",
        "„Bildschirmpräsentation aufzeichnen“ geht noch einen Schritt weiter: Es zeichnet " +
          "Anzeigedauern, Sprechertext und auf Wunsch ein Kamerabild auf. Über „Datei“ → " +
          "„Exportieren“ → „Video erstellen“ wird daraus ein Film, der ohne PowerPoint läuft.",
      ],
      keyPoints: [
        "Einrichten → Anzeigedauern verwenden, wenn vorhanden.",
        "Aufzeichnen: Zeiten, Sprechertext, Kamera – Export als Video.",
      ],
      task: {
        instruction: "Stelle in der Kiosk-Kopie ein, dass die Anzeigedauern verwendet werden.",
        hint: "Bildschirmpräsentation einrichten → Nächste Folie: „Anzeigedauern verwenden, wenn vorhanden“.",
        macro: MAKRO_BILDSCHIRMPRAESENTATION,
        check: () =>
          pruefeMitMakro(MAKRO_BILDSCHIRMPRAESENTATION, (bericht) =>
            fromCriteria(
              [
                // VBA: ppSlideShowUseSlideTimings = 2
                criterion("„Anzeigedauern verwenden, wenn vorhanden“ ist eingestellt", bericht.zahl("weiter") === 2),
                criterion(`Folien mit Anzeigedauer: ${bericht.zahl("mitAnzeigedauer") ?? 0}`, (bericht.zahl("mitAnzeigedauer") ?? 0) > 0),
              ],
              {
                success: "Die Präsentation läuft jetzt von selbst – im eingeübten Tempo.",
                failure: "Die Anzeigedauern werden noch nicht verwendet.",
              },
            ),
          ),
      },
    },
  ],
};
